import { useAutoAnimate } from "@formkit/auto-animate/react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Stack, useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import dayjs from "dayjs";
import { groupBy } from "lodash";
import { useMemo, useState } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { TypeLabel } from "@/Components/v2/atoms/TypeLabel";
import { Typography } from "@/Components/v2/atoms/Typography";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { useConfig } from "@/hooks/useConfig";
import { formatDate } from "@/pages/scenario-builder/utils";

import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
} from "../../components/StyledComponents";
import { bcpLayerKey, getBusinessUnitHierarchy } from "../../utils";
import {
  useReportSectionTracking,
  useScenarioAnalyticsV3,
} from "@/pages/scenario-builder/v3/utils/useScenarioAnalyticsV3";
import HistoricSpendChange from "@/pages/scenario-builder/v3/components/HistoricSpendChange/HistoricSpendChange";
import { ScenarioType, ScenarioTypeEnum } from "../scenario-form/types";
import { useScenarioResultsContext } from "./ScenarioResultsProvider";

// Custom hook for accordion state management
const useAccordionState = () => {
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

  const handleAccordionChange =
    (unitId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) =>
        isExpanded ? [...prev, unitId] : prev.filter((id) => id !== unitId)
      );
    };

  const isExpanded = (unitId: string) => expandedPanels.includes(unitId);

  return { handleAccordionChange, isExpanded };
};

export const ScenarioInputsPage = () => {
  const [parent] = useAutoAnimate({ duration: 275 });
  const { handleAccordionChange, isExpanded } = useAccordionState();
  const { scenario, isScenarioLoading, bcpKeyToNode } = useScenarioResultsContext();
  const { value: scenarioBuilderEnableNewConstraints } = useFeatureGate(
    featureFlag.scenarioBuilderConstraintsFromApi
  );
  // Initialize v3 analytics
  const { trackReportPageDuration } = useScenarioAnalyticsV3({ scenario });

  // Track report section viewing duration
  useReportSectionTracking(trackReportPageDuration, "Scenario Inputs Page", {
    trackingInterval: 30_000, // 30 seconds
    minimumDuration: 3_000, // 3 seconds
  });

  const constraintsByBCPKey = useMemo(
    () =>
      groupBy(scenario?.scenarioConstraints, (constraint) =>
        bcpLayerKey({
          brand: constraint.brand ?? "",
          category: constraint.category ?? "",
          product: constraint.product ?? "",
        })
      ),
    [scenario?.scenarioConstraints]
  );

  const { config } = useConfig();
  const theme = useTheme();

  // Get target node for business unit display
  const targetNode = scenario?.targetNode;

  const { mapDefaultNodeName, mapBcpName } = useDefaultNodeNameInfo();

  // Format scenario information
  const scenarioInfo = useMemo(() => {
    return {
      name: scenario?.name || "Untitled Scenario",
      description: scenario?.description || "-",
      businessUnit: mapDefaultNodeName(targetNode?.brand ?? "default"),
      geography: scenario?.geography || "National",
      startDate: scenario?.startDate ? formatDate(new Date(scenario.startDate)) : "N/A",
      endDate: scenario?.endDate ? formatDate(new Date(scenario.endDate)) : "N/A",
      totalBudget: scenario?.value || 0,
    };
  }, [scenario, targetNode, mapDefaultNodeName]);

  // Get scenario type styling and label
  const scenarioTypeInfo = useMemo(() => {
    const scenarioType = (scenario?.scenarioType as ScenarioTypeEnum) || ScenarioTypeEnum.Unknown;
    const style = ScenarioType.getStyle(scenarioType, theme);
    const label = ScenarioType.getDisplayName(scenarioType);

    return { label, labelColor: style.labelColor };
  }, [scenario?.scenarioType, theme]);

  const hasScenarios = useMemo(() => {
    return scenario?.scenarioConstraints && scenario.scenarioConstraints.length > 0;
  }, [scenario?.scenarioConstraints]);

  const formatCurrency = (value: number) => {
    return config?.currency?.formatCurrency?.(value) || `$${value.toLocaleString()}`;
  };

  // Show loading state
  if (isScenarioLoading) {
    return <CircularProgress size={150} sx={{ marginTop: "30rem" }} />;
  }

  // Show error state
  if (!scenario) {
    return <div>Scenario or nodes not found</div>;
  }

  const monthDifference =
    scenario.endDate && scenario.startDate
      ? dayjs(scenario.endDate).diff(dayjs(scenario.startDate), "month") + 1
      : 0;

  return (
    <Stack mb={2}>
      <Typography
        variant="h6"
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          color: theme.palette.text.primary,
          marginBottom: theme.spacing(2),
        }}
      >
        Information
      </Typography>

      <Stack
        sx={{
          gap: theme.spacing(2),
          marginBottom: theme.spacing(3),
        }}
      >
        <Stack
          sx={{
            gap: theme.spacing(0.5),
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Scenario Name
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              color: theme.palette.text.primary,
            }}
          >
            {scenarioInfo.name}
          </Typography>
        </Stack>

        <Stack
          sx={{
            gap: theme.spacing(0.5),
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Scenario Description
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              color: theme.palette.text.primary,
            }}
          >
            {scenarioInfo.description}
          </Typography>
        </Stack>

        <Stack
          sx={{
            gap: theme.spacing(0.5),
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Scenario Type
          </Typography>
          <Box>
            <TypeLabel label={scenarioTypeInfo.label} labelColor={scenarioTypeInfo.labelColor} />
          </Box>
        </Stack>

        <Stack
          sx={{
            gap: theme.spacing(0.5),
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Business Unit
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              color: theme.palette.text.primary,
            }}
          >
            {mapDefaultNodeName(scenarioInfo.businessUnit)}
          </Typography>
        </Stack>

        <Stack
          sx={{
            gap: theme.spacing(0.5),
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Geographic Scope
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              color: theme.palette.text.primary,
            }}
          >
            {scenarioInfo.geography}
          </Typography>
        </Stack>

        <Stack
          sx={{
            flexDirection: "row",
            gap: theme.spacing(2),
          }}
        >
          <Stack
            sx={{
              gap: theme.spacing(0.5),
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              Start
            </Typography>
            <Typography
              sx={{
                fontSize: "1rem",
                color: theme.palette.text.primary,
              }}
            >
              {scenarioInfo.startDate}
            </Typography>
          </Stack>

          <Stack
            sx={{
              gap: theme.spacing(0.5),
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              End
            </Typography>
            <Typography
              sx={{
                fontSize: "1rem",
                color: theme.palette.text.primary,
              }}
            >
              {scenarioInfo.endDate}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack
        sx={{
          gap: theme.spacing(2),
          marginBottom: 1.5,
        }}
      >
        <Stack
          sx={{
            gap: theme.spacing(0.5),
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Scenario Duration
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              color: theme.palette.text.primary,
            }}
          >
            {monthDifference} months
          </Typography>
        </Stack>
      </Stack>

      <Typography
        variant="h6"
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          color: theme.palette.text.primary,
          marginBottom: theme.spacing(2),
          marginTop: theme.spacing(2),
        }}
      >
        Budget
      </Typography>

      <Stack
        sx={{
          gap: theme.spacing(0.5),
        }}
      >
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          Total Budget
        </Typography>
        <Typography
          sx={{
            fontSize: "1rem",
            color: theme.palette.text.primary,
          }}
        >
          {formatCurrency(scenarioInfo.totalBudget)}
        </Typography>
      </Stack>

      <Typography
        variant="h6"
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          color: theme.palette.text.primary,
          marginBottom: theme.spacing(2),
          marginTop: theme.spacing(4),
        }}
      >
        Inputs
      </Typography>

      <Typography
        sx={{
          fontSize: "1rem",
          fontWeight: 600,
          marginBottom: theme.spacing(1),
        }}
      >
        Channel Inputs
      </Typography>

      <Box sx={{ marginBottom: theme.spacing(2) }}>
        {hasScenarios ? (
          <div ref={parent}>
            {Object.entries(constraintsByBCPKey).map(([bcpKey, constraints]) => {
              const bcp = bcpKeyToNode?.[bcpKey];
              if (!bcp) {
                console.error("BCP not found", bcpKey);
                return null;
              }

              return (
                <StyledAccordion
                  key={bcpKey}
                  expanded={isExpanded(bcpKey)}
                  onChange={handleAccordionChange(bcpKey)}
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      flexDirection: "row-reverse",
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        margin: 0,
                      },
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        marginRight: 1,
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 400, fontSize: "1rem", flex: 1 }}
                    >
                      {getBusinessUnitHierarchy(bcp, mapBcpName)}
                    </Typography>
                  </StyledAccordionSummary>

                  <StyledAccordionDetails>
                    {(() => {
                      const hasGeography = Boolean(constraints[0]?.geography);
                      const isOptimizer = scenario.scenarioType === ScenarioTypeEnum.Optimizer;

                      return (
                        <>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "2fr 1fr 1fr",
                              gap: theme.spacing(1),
                              padding: theme.spacing(1.5),
                              backgroundColor: theme.palette.tableColumnHeader.background,
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {hasGeography && (
                              <Box sx={{ color: theme.palette.text.primary }}>Geography</Box>
                            )}
                            <Box sx={{ color: theme.palette.text.primary }}>Channel</Box>
                            {isOptimizer ? (
                              <>
                                <Box sx={{ color: theme.palette.text.primary }}>Min Value</Box>
                                <Box sx={{ color: theme.palette.text.primary }}>Max Value</Box>
                              </>
                            ) : (
                              <Box sx={{ color: theme.palette.text.primary }}>Budget</Box>
                            )}
                          </Box>

                          {constraints.map((constraint, index) => (
                            <Box
                              key={`${constraint.brand}-${constraint.category}-${constraint.product}-${constraint.channel}-${constraint.geography}-${index}`}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "2fr 1fr 1fr",
                                gap: theme.spacing(1),
                                padding: theme.spacing(1.5),
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                fontSize: "0.875rem",
                                color: theme.palette.text.primary,
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                                "&:last-child": {
                                  borderBottom: "none",
                                },
                              }}
                            >
                              {constraint.geography && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  {constraint.geography}
                                </Box>
                              )}
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                {constraint.channel}
                              </Box>
                              {isOptimizer ? (
                                <>
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {constraint.minValue
                                      ? formatCurrency(constraint.minValue)
                                      : formatCurrency(0)}
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {constraint.maxValue
                                      ? formatCurrency(constraint.maxValue)
                                      : formatCurrency(0)}
                                  </Box>
                                </>
                              ) : (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  {constraint.maxValue
                                    ? formatCurrency(constraint.maxValue)
                                    : formatCurrency(0)}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </>
                      );
                    })()}
                  </StyledAccordionDetails>
                </StyledAccordion>
              );
            })}
          </div>
        ) : (
          <Box sx={{ textAlign: "left", color: "text.secondary" }}>
            No channel constraints defined for this scenario.
          </Box>
        )}
      </Box>

      {scenarioBuilderEnableNewConstraints && (
        <HistoricSpendChange scenario={scenario} distinctBCP={bcpKeyToNode} />
      )}
    </Stack>
  );
};
